/**
 * FieldsTab Component
 *
 * Displays field definitions in a sortable, filterable table.
 */

import React, { useState, useMemo } from 'react'
import { Table, Input, Space, Switch, Typography, Tooltip, Popover, Descriptions, Tag } from 'antd'
import {
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  LockOutlined
} from '@ant-design/icons'
import type { ColumnsType, TableProps } from 'antd/es/table'
import type { FieldMetadata } from '@/services/metadataService'
import type { TabProps } from '../types'

const { Text } = Typography

/**
 * Field row for table display
 */
interface FieldRow {
  key: string
  name: string
  field: FieldMetadata
}

/**
 * Type badge colors
 */
const TYPE_COLORS: Record<string, string> = {
  string: 'blue',
  str: 'blue',
  uuid: 'cyan',
  integer: 'green',
  int: 'green',
  numeric: 'green',
  float: 'green',
  Decimal: 'green',
  bool: 'orange',
  boolean: 'orange',
  timestamp: 'purple',
  timestamptz: 'purple',
  date: 'purple',
  datetime: 'purple',
  json: 'magenta',
  jsonb: 'magenta',
  array: 'volcano'
}

/**
 * Tenant roles (per-organization)
 */
const TENANT_ROLES = new Set(['owner', 'admin', 'sales', 'pricer', 'datamgr', 'viewer'])

/**
 * Global roles (platform-wide)
 */
const GLOBAL_ROLES = new Set(['rp_admin', 'rp_support', 'system'])

/**
 * Check if a field has custom access restrictions
 */
function hasCustomPermissions(field: FieldMetadata): boolean {
  const hasReadRoles = field.read_roles && field.read_roles.length > 0
  const hasWriteRoles = field.write_roles && field.write_roles.length > 0
  return hasReadRoles || hasWriteRoles || false
}

/**
 * Categorize roles into tenant and global groups
 */
function categorizeRoles(roles: string[]): { tenant: string[]; global: string[]; special: string[] } {
  const tenant: string[] = []
  const global: string[] = []
  const special: string[] = []

  for (const role of roles) {
    if (TENANT_ROLES.has(role)) {
      tenant.push(role)
    } else if (GLOBAL_ROLES.has(role)) {
      global.push(role)
    } else {
      // Special roles like $org_owner
      special.push(role)
    }
  }

  return { tenant, global, special }
}

/**
 * Boolean indicator component
 */
function BooleanIndicator({ value, trueLabel, falseLabel }: {
  value?: boolean
  trueLabel?: string
  falseLabel?: string
}): React.ReactElement {
  if (value === true) {
    return (
      <Tooltip title={trueLabel || 'Yes'}>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
      </Tooltip>
    )
  }
  if (value === false) {
    return (
      <Tooltip title={falseLabel || 'No'}>
        <CloseCircleOutlined style={{ color: '#d9d9d9' }} />
      </Tooltip>
    )
  }
  return <Text type="secondary">-</Text>
}

/**
 * Render categorized roles display
 */
function RolesDisplay({ roles, emptyMessage }: { roles: string[] | undefined; emptyMessage: string }): React.ReactElement {
  if (!roles || roles.length === 0) {
    return <Text type="secondary" italic style={{ fontSize: '12px' }}>{emptyMessage}</Text>
  }

  const { tenant, global, special } = categorizeRoles(roles)

  return (
    <Space direction="vertical" size={2} style={{ width: '100%' }}>
      {tenant.length > 0 && (
        <div>
          <Text type="secondary" style={{ fontSize: '11px' }}>Tenant: </Text>
          <Text style={{ fontSize: '12px' }}>{tenant.join(', ')}</Text>
        </div>
      )}
      {global.length > 0 && (
        <div>
          <Text type="secondary" style={{ fontSize: '11px' }}>Global: </Text>
          <Text style={{ fontSize: '12px' }}>{global.join(', ')}</Text>
        </div>
      )}
      {special.length > 0 && (
        <div>
          <Text type="secondary" style={{ fontSize: '11px' }}>Special: </Text>
          <Text style={{ fontSize: '12px' }}>{special.join(', ')}</Text>
        </div>
      )}
    </Space>
  )
}

/**
 * Field detail popover - shows additional field metadata
 * NOTE: db_column intentionally NOT shown - internal implementation detail
 */
function FieldDetailPopover({ field, name }: { field: FieldMetadata; name: string }): React.ReactElement {
  const hasPermissions = hasCustomPermissions(field)

  const content = (
    <Descriptions column={1} size="small" style={{ width: '320px' }}>
      {field.description && (
        <Descriptions.Item label="Description">
          {field.description}
        </Descriptions.Item>
      )}
      {field.default !== undefined && (
        <Descriptions.Item label="Default">
          <Text code>{JSON.stringify(field.default)}</Text>
        </Descriptions.Item>
      )}
      {field.allowed_values && field.allowed_values.length > 0 && (
        <Descriptions.Item label="Allowed Values">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {field.allowed_values.slice(0, 5).map((v, i) => (
              <Tag key={i} style={{ margin: 0 }}>
                {typeof v === 'string' ? v : v.label}
              </Tag>
            ))}
            {field.allowed_values.length > 5 && (
              <Tag style={{ margin: 0 }}>+{field.allowed_values.length - 5} more</Tag>
            )}
          </div>
        </Descriptions.Item>
      )}
      {field.regex && (
        <Descriptions.Item label="Regex">
          <Text code>{field.regex}</Text>
        </Descriptions.Item>
      )}
      {(field.min_value !== undefined || field.max_value !== undefined) && (
        <Descriptions.Item label="Range">
          {field.min_value ?? '-'} to {field.max_value ?? '-'}
        </Descriptions.Item>
      )}
      {(field.min_length !== undefined || field.max_length !== undefined) && (
        <Descriptions.Item label="Length">
          {field.min_length ?? '0'} - {field.max_length ?? 'unlimited'}
        </Descriptions.Item>
      )}
      {field.is_fk && (
        <Descriptions.Item label="Foreign Key">
          <Tag color="purple">{field.fk_target_model}</Tag>
        </Descriptions.Item>
      )}

      {/* Access Restrictions Section - always show */}
      <Descriptions.Item
        label={
          <Space size={4}>
            <LockOutlined style={{ color: hasPermissions ? '#faad14' : '#d9d9d9' }} />
            <span>Read</span>
          </Space>
        }
      >
        <RolesDisplay roles={field.read_roles} emptyMessage="Inherits table permissions" />
      </Descriptions.Item>
      <Descriptions.Item
        label={
          <Space size={4}>
            <LockOutlined style={{ color: hasPermissions ? '#faad14' : '#d9d9d9' }} />
            <span>Write</span>
          </Space>
        }
      >
        <RolesDisplay roles={field.write_roles} emptyMessage="Inherits table permissions" />
      </Descriptions.Item>
    </Descriptions>
  )

  return (
    <Popover content={content} title={name} trigger="click" placement="right">
      <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
    </Popover>
  )
}

export function FieldsTab({ metadata }: TabProps): React.ReactElement {
  const [searchTerm, setSearchTerm] = useState('')
  const [showHiddenFields, setShowHiddenFields] = useState(false)

  // Convert fields to table data
  // Now that API returns ALL fields (allow_system=true), we filter client-side
  const tableData: FieldRow[] = useMemo(() => {
    return Object.entries(metadata.fields)
      .map(([name, field]) => ({
        key: name,
        name,
        field
      }))
      .filter((row) => {
        // Filter by hidden field toggle
        // Hidden fields: is_visible=false OR system=true
        // When toggle is OFF, show only visible non-system fields
        // When toggle is ON, show ALL fields
        if (!showHiddenFields) {
          const isHidden = row.field.is_visible === false || row.field.system === true
          if (isHidden) {
            return false
          }
        }
        // Filter by search term
        if (searchTerm) {
          const term = searchTerm.toLowerCase()
          return (
            row.name.toLowerCase().includes(term) ||
            row.field.title?.toLowerCase().includes(term) ||
            row.field.py_type.toLowerCase().includes(term)
          )
        }
        return true
      })
  }, [metadata.fields, searchTerm, showHiddenFields])

  // Table columns
  const columns: ColumnsType<FieldRow> = [
    {
      title: 'Field Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string, record: FieldRow) => (
        <Space>
          <Text strong style={{ fontFamily: 'monospace' }}>{name}</Text>
          {record.field.is_fk && (
            <Tooltip title={`FK -> ${record.field.fk_target_model}`}>
              <KeyOutlined style={{ color: '#722ed1' }} />
            </Tooltip>
          )}
          {hasCustomPermissions(record.field) && (
            <Tooltip title="Field has custom access restrictions">
              <LockOutlined style={{ color: '#faad14' }} />
            </Tooltip>
          )}
          <FieldDetailPopover field={record.field} name={name} />
        </Space>
      ),
      width: 220
    },
    {
      title: 'Type',
      key: 'type',
      sorter: (a, b) => a.field.py_type.localeCompare(b.field.py_type),
      render: (_: any, record: FieldRow) => {
        const type = record.field.py_type
        const color = TYPE_COLORS[type] || 'default'
        return (
          <Space>
            <Tag color={color}>{type}</Tag>
            {record.field.is_array && <Tag color="volcano">[]</Tag>}
          </Space>
        )
      },
      width: 120
    },
    {
      title: 'Title',
      key: 'title',
      sorter: (a, b) => (a.field.title || '').localeCompare(b.field.title || ''),
      render: (_: any, record: FieldRow) => (
        <Text>{record.field.title || <Text type="secondary">-</Text>}</Text>
      ),
      width: 150
    },
    {
      title: (
        <Tooltip title="Required on Create">
          <span>Req</span>
        </Tooltip>
      ),
      key: 'required',
      align: 'center' as const,
      sorter: (a, b) => Number(a.field.required_on_create) - Number(b.field.required_on_create),
      render: (_: any, record: FieldRow) => (
        <BooleanIndicator value={record.field.required_on_create} trueLabel="Required" falseLabel="Optional" />
      ),
      width: 60
    },
    {
      title: (
        <Tooltip title="Visible by Default">
          <EyeOutlined />
        </Tooltip>
      ),
      key: 'visible',
      align: 'center' as const,
      sorter: (a, b) => Number(a.field.is_visible !== false) - Number(b.field.is_visible !== false),
      render: (_: any, record: FieldRow) => (
        record.field.is_visible === false ? (
          <Tooltip title="Hidden by default">
            <EyeInvisibleOutlined style={{ color: '#d9d9d9' }} />
          </Tooltip>
        ) : (
          <Tooltip title="Visible by default">
            <EyeOutlined style={{ color: '#52c41a' }} />
          </Tooltip>
        )
      ),
      width: 50
    }
  ]

  // Table change handler for sorting
  const handleTableChange: TableProps<FieldRow>['onChange'] = () => {
    // Sorting handled by antd
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '16px'
      }}>
        <Input
          placeholder="Search fields..."
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          style={{ maxWidth: '300px' }}
        />
        <Space>
          <Text type="secondary">Show hidden fields:</Text>
          <Switch
            checked={showHiddenFields}
            onChange={setShowHiddenFields}
            size="small"
          />
        </Space>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={tableData}
        size="small"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} fields`
        }}
        onChange={handleTableChange}
        scroll={{ x: 'max-content' }}
        rowClassName={(record) =>
          record.field.is_visible === false ? 'metadata-inspector-hidden-row' : ''
        }
      />

      {/* Summary */}
      <div style={{
        marginTop: '12px',
        padding: '8px 12px',
        background: '#fafafa',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#8c8c8c'
      }}>
        <Space split={<span style={{ color: '#d9d9d9' }}>|</span>}>
          <span>Total: {Object.keys(metadata.fields).length} fields</span>
          <span>
            Visible: {Object.values(metadata.fields).filter(f => f.is_visible !== false && f.system !== true).length}
          </span>
          <span>
            Hidden: {Object.values(metadata.fields).filter(f => f.is_visible === false || f.system === true).length}
          </span>
          <span>
            Required: {Object.values(metadata.fields).filter(f => f.required_on_create).length}
          </span>
        </Space>
      </div>
    </div>
  )
}

export default FieldsTab
